import React, { useState } from 'react'
import { X } from 'lucide-react'

export interface TagItemData {
    key: string
    label: string
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    disabled?: boolean
}

export interface TagGroupProps {
    items: TagItemData[]
    label?: string
    description?: string
    errorMessage?: string
    isInvalid?: boolean
    size?: 'sm' | 'md' | 'lg'
    variant?: 'default' | 'surface'
    selectionMode?: 'none' | 'single' | 'multiple'
    selectedKeys?: Set<string>
    defaultSelectedKeys?: Set<string>
    onSelectionChange?: (keys: Set<string>) => void
    onRemove?: (key: string) => void
    style?: React.CSSProperties
}

const sizeConfig = {
    sm: 'rounded-xl px-1 py-0.5 text-xs leading-[1.34]',
    md: 'rounded-xl px-1.5 py-1 text-xs leading-[1.34]',
    lg: 'rounded-2xl px-2.5 py-1.5 text-sm leading-[1.43]',
}

export const TagGroup = ({
    items,
    label,
    description,
    errorMessage,
    isInvalid = false,
    size = 'md',
    variant = 'default',
    selectionMode = 'none',
    selectedKeys: controlledKeys,
    defaultSelectedKeys = new Set<string>(),
    onSelectionChange,
    onRemove,
    style,
}: TagGroupProps) => {
    const [internalKeys, setInternalKeys] = useState<Set<string>>(defaultSelectedKeys)
    const selected = controlledKeys ?? internalKeys

    const handleSelect = (key: string) => {
        if (selectionMode === 'none') return
        const next = new Set(selected)
        if (selectionMode === 'single') {
            if (next.has(key)) next.clear()
            else {
                next.clear()
                next.add(key)
            }
        } else {
            if (next.has(key)) next.delete(key)
            else next.add(key)
        }
        if (!controlledKeys) setInternalKeys(next)
        onSelectionChange?.(next)
    }

    return (
        <div className="flex flex-col gap-1" style={style}>
            {label && <label className="inline-flex flex-wrap items-center gap-1 pr-2 text-sm font-medium leading-[1.43] text-foreground">{label}</label>}

            <div className="flex flex-wrap items-center gap-1.5">
                {items.map((item) => (
                    <TagItem
                        key={item.key}
                        item={item}
                        isSelected={selected.has(item.key)}
                        sizeClass={sizeConfig[size]}
                        variant={variant}
                        disabled={item.disabled}
                        onClick={() => !item.disabled && handleSelect(item.key)}
                        onRemove={onRemove ? () => onRemove(item.key) : undefined}
                    />
                ))}
            </div>

            {isInvalid && errorMessage ? (
                <div className="p-1">
                    <p className="m-0 text-xs font-normal leading-[1.34] text-danger">{errorMessage}</p>
                </div>
            ) : description ? (
                <div className="p-1">
                    <p className="m-0 text-xs font-normal leading-[1.34] text-foreground-muted">{description}</p>
                </div>
            ) : null}
        </div>
    )
}

const TagItem = ({
    item,
    isSelected,
    sizeClass,
    variant,
    disabled,
    onClick,
    onRemove,
}: {
    item: TagItemData
    isSelected: boolean
    sizeClass: string
    variant: 'default' | 'surface'
    disabled?: boolean
    onClick: () => void
    onRemove?: () => void
}) => {
    const unselectedBg = variant === 'surface' ? 'bg-surface' : 'bg-default'

    return (
        <div
            role="option"
            aria-selected={isSelected}
            onClick={onClick}
            className={`inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap font-medium transition-colors duration-100
                ${sizeClass}
                ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                ${isSelected ? 'bg-accent-soft text-accent' : `${unselectedBg} text-foreground ${disabled ? '' : 'hover:bg-default-hover'}`}`}
        >
            {item.prefix && <span className="flex shrink-0 items-center">{item.prefix}</span>}
            {item.label}
            {item.suffix && <span className="flex shrink-0 items-center">{item.suffix}</span>}
            {onRemove && !disabled && (
                <span
                    onClick={(e) => {
                        e.stopPropagation()
                        onRemove()
                    }}
                    className="flex cursor-pointer items-center"
                >
                    <X size={16} strokeWidth={1.5} />
                </span>
            )}
        </div>
    )
}
